import * as React from "react";
import { ITriggerManualJob } from "../../../redux/types/ITriggerManualJob";
import TextDataInput from "../../../repertoire/components/textDataInput/TextDataInput";
import { triggerManualJob } from "../../../redux/thunks/HomeThunks";
import { ComboBox } from "@fluentui/react";
import { SaveAndClose } from "./SaveAndClose";
import { AGREEMENT_CATEGORY_LOOKUP } from "../../../lookup/Consts";
import { getLookupValues } from '../../../lookup/services/LookupHelpers';
import { ILookupDictionary } from "../../../lookup/types/ILookupDictionary";

export interface IGenerateStatementDetailsParametersProps {
    createNewJob: () => any;
    triggerManualJob: typeof triggerManualJob;
    manualJobParameters: ITriggerManualJob;
    formControls: any;
    jobType?: string;
    enableMemberNumber?: boolean;
    enableRollUpTerritories?: boolean;
    scheduledJobSet?: (value: boolean) => void;
    onError?: (errorMessage: string) => void;
    lookupValues: ILookupDictionary;
}

const RIGHT_TYPE_CAPTION = 'Right Type';
const AGREEMENT_CATEGORY_CAPTION = 'Agreement Category';
const ROLL_UP_TERRITORIES_CAPTION = 'Roll Up Territories';
const ALL_KEY = 'ALL';
const ALL_OPTION = { key: ALL_KEY, text: 'All' };

const GenerateStatementDetailsParameters = (props: IGenerateStatementDetailsParametersProps) => {
    const agreementCategoryOptions = React.useMemo(() => {
        if (!props.lookupValues) return [ALL_OPTION];
        const lookupItems = getLookupValues(AGREEMENT_CATEGORY_LOOKUP, props.lookupValues) ?? [];
        const fromLookup = lookupItems.map(item => ({ key: item.code, text: item.description }));
        return [ALL_OPTION, ...fromLookup];
    }, [props.lookupValues]);

    const realOptionKeys = React.useMemo(
        () => agreementCategoryOptions.filter(o => o.key !== ALL_KEY).map(o => o.key),
        [agreementCategoryOptions]
    );

    const [data, setData] = React.useState(() => {
        return props.formControls.map(item => {
            const newItem = { ...item };
            if (item.Type === 'combobox') {
                newItem.CurrentValue = item.CurrentValue || '';
            }
            if (item.Caption === 'Payment Run') {
                newItem.CurrentValue = item.CurrentValue 
                    ? (Array.isArray(item.CurrentValue) ? item.CurrentValue : [item.CurrentValue])
                    : [];
            }
            if (item.Caption === 'Member Numbers') {
                const doUndoItem = props.formControls.find(i => i.Caption === 'Do/Undo Generation');
                newItem.Disabled = !props.enableMemberNumber || doUndoItem?.CurrentValue !== 'Do';
            }
            if (item.Caption === AGREEMENT_CATEGORY_CAPTION) {
                newItem.CurrentValue = [...realOptionKeys];
            }
            return newItem;
        });
    });

    const [generalError, setGeneralError] = React.useState<string[]>([]);

    const isCRDFormat = React.useMemo(() => {
        const formatItem = data.find(i => i.Caption === 'Format');
        return formatItem?.CurrentValue === 'CRD';
    }, [data]);

    const memberNumberHasValue = React.useMemo(() => {
        const memberItem = data.find(i => i.Caption === 'Member Numbers');
        return !memberItem?.Disabled && typeof memberItem?.CurrentValue === 'string' && memberItem.CurrentValue.trim() !== '';
    }, [data]);

    const showHiddenDropdowns = isCRDFormat && memberNumberHasValue;

    const handleTriggerManualJob = async (params) => {
        const errors = validateFields();
        if (errors.length === 0) {
            try { 
                await props.triggerManualJob(data);
                props.scheduledJobSet(true);
                return true;
            } catch (err) {
                props.onError?.(err?.toString() || "An error occurred");
                return false;
            }
        }
        return false;
    };

    const validateFields = (): string[] => {
        const format = data.find(item => item.Caption === 'Format');
        const undoItem = data.find(item => item.Caption === 'Do/Undo Generation');
        const paymentRun = data.find(item => item.Caption === 'Payment Run');
        const memberNumberItem = data.find(item => item.Caption === 'Member Numbers');

        const memberNumbers = typeof memberNumberItem?.CurrentValue === 'string' 
            ? memberNumberItem.CurrentValue.trim() 
            : '';
        
        const disallowedDelimiters = [' ', ',', '.', ':'];
        let errors: string[] = [];

        if (!format || format.CurrentValue === '') {
            errors.push('Format is required');
        }
        if (!undoItem || undoItem.CurrentValue === '') {
            errors.push('Do/Undo Generation is required');
        }
        if (!paymentRun || 
            (Array.isArray(paymentRun.CurrentValue) && paymentRun.CurrentValue.length === 0)) {
            errors.push('Payment Run is required');
        }
        if (!memberNumberItem?.Disabled && memberNumbers) {
            if (disallowedDelimiters.some(delim => memberNumbers.includes(delim))) {
                errors.push('Member Numbers contain invalid delimiters');
            } else {
                const detectedDelimiter = memberNumbers.match(/[^a-zA-Z0-9]/)?.[0];
                const segments = detectedDelimiter ? memberNumbers.split(detectedDelimiter) : [memberNumbers];
                const allAlphaNumeric = segments.every(entry => /^[a-zA-Z0-9]+$/.test(entry.trim()));
                if (!allAlphaNumeric) {
                    errors.push('Each Member Number must be alphanumeric only');
                }
            }
        }
        setGeneralError(errors);
        return errors;
    };

    const handleChange = (formItem: any, value?: any) => {
        setData(prevState => {
            const prevFormatItem = prevState.find(i => i.Caption === 'Format');
            const prevFormat = prevFormatItem?.CurrentValue;

            const newFormat = formItem.Caption === 'Format'
                ? (typeof value === 'object' ? value.key : value)
                : prevFormat;

            let newState = prevState.map(item => {
                if (formItem.Caption === 'Do/Undo Generation') {
                    if (item.Caption === 'Member Numbers') {
                        const newValue = typeof value === 'object' ? value.key : value;
                        return {
                            ...item,
                            CurrentValue: newValue === 'Undo' ? '' : item.CurrentValue,
                            Disabled: newValue !== 'Do' || !props.enableMemberNumber
                        };
                    }
                }

                if (item.Caption === AGREEMENT_CATEGORY_CAPTION && formItem.Caption === AGREEMENT_CATEGORY_CAPTION) {
                    if (newFormat !== 'CRD') {
                        return { ...item, CurrentValue: [...realOptionKeys] };
                    }

                    if (value === ALL_KEY) {
                        if (realOptionKeys.length > 0 && realOptionKeys.every(k => item.CurrentValue.includes(k))) {
                            return { ...item, CurrentValue: [] };
                        } else {
                            return { ...item, CurrentValue: [...realOptionKeys] };
                        }
                    } else {
                        return {
                            ...item,
                            CurrentValue: item.CurrentValue.includes(value)
                                ? item.CurrentValue.filter(v => v !== value && v !== ALL_KEY)
                                : [...item.CurrentValue, value]
                        };
                    }
                }

                if (item.Caption === RIGHT_TYPE_CAPTION && newFormat !== 'CRD') {
                    return { ...item, CurrentValue: 'BOT' };
                }

                if (item.Caption === ROLL_UP_TERRITORIES_CAPTION && newFormat !== 'CRD') {
                    return { ...item, CurrentValue: ALL_KEY };
                }

                if (item.Caption === 'Payment Run' && formItem.Caption === 'Payment Run') {
                    const isCRD = newFormat === 'CRD';
                    if (isCRD) {
                        if (Array.isArray(value.value)) {
                            return { ...item, CurrentValue: value.value };
                        }
                        const currentValues = Array.isArray(item.CurrentValue) ? item.CurrentValue : [];
                        const valueKey = value.key;
                        return {
                            ...item,
                            CurrentValue: currentValues.includes(valueKey)
                                ? currentValues.filter(v => v !== valueKey)
                                : [...currentValues, valueKey]
                        };
                    } else {
                        if (prevFormat === 'CRD') {
                            return { ...item, CurrentValue: [] };
                        }
                        return { ...item, CurrentValue: value.key ? [value.key] : [] };
                    }
                }

                if (item.Type === 'combobox' && item.Caption !== 'Payment Run') {
                    if (item.Caption === formItem.Caption) {
                        return {
                            ...item,
                            CurrentValue: typeof value === 'object' ? value.key : value
                        };
                    }
                }
                
                if (item.Caption === 'Payment Run' && item.Caption === formItem.Caption) {
                    const isCRD = newFormat === 'CRD';
                    if (isCRD) {
                        if (Array.isArray(value.value)) {
                            return { ...item, CurrentValue: value.value };
                        }
                        const currentValues = Array.isArray(item.CurrentValue) ? item.CurrentValue : [];
                        const valueKey = value.key;
                        return {
                            ...item,
                            CurrentValue: currentValues.includes(valueKey)
                                ? currentValues.filter(v => v !== valueKey)
                                : [...currentValues, valueKey]
                        };
                    } else {
                        const currentValue = Array.isArray(item.CurrentValue) 
                            ? item.CurrentValue[0] 
                            : item.CurrentValue;
                        const newValue = value.key ? [value.key] : [];
                        return {
                            ...item,
                            CurrentValue: newValue.length > 0 ? newValue : currentValue ? [currentValue] : []
                        };
                    }
                }

                if (item.Caption === formItem.Caption) {
                    return { ...item, CurrentValue: value };
                }

                return item;
            });

            if (formItem.Caption === 'Do/Undo Generation') {
                const doUndoValue = typeof value === 'object' ? value.key : value;
                const memberNumbersItem = newState.find(item => item.Caption === 'Member Numbers');
                if (memberNumbersItem) {
                    memberNumbersItem.Disabled = doUndoValue !== 'Do' || !props.enableMemberNumber;
                }
            }

            return newState;
        });
    };

    const getFormComponents = (jobControls) => {
        const components = jobControls.map((formItem) => {
            const controlWithOptions = formItem.Options && Array.isArray(formItem.Options);
            let optionList = [];
            if (formItem.Caption === 'Payment Run') {
                const formatItem = data.find(item => item.Caption === 'Format');
                const isCRD = formatItem?.CurrentValue === 'CRD';
                const currentValues = Array.isArray(formItem.CurrentValue) ? formItem.CurrentValue : [];

                const optionList = formItem.DataSourceRepo?.paymentRuns?.map((option) => ({
                    key: option.code,
                    text: option.description,
                    selected: isCRD ? currentValues.includes(option.code) : false
                })) || [];

                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12" key={formItem.Caption}>
                        <ComboBox
                            label={formItem.Caption}
                            options={optionList}
                            selectedKey={
                                isCRD
                                    ? undefined
                                    : currentValues.length > 0
                                        ? currentValues[0]
                                        : undefined
                            }
                            onChange={(e, option) => {
                                if (option) {
                                    if (isCRD) {
                                        const newValues = currentValues.includes(option.key)
                                            ? currentValues.filter(v => v !== option.key)
                                            : [...currentValues, option.key];
                                        handleChange(formItem, { key: option.key, value: newValues });
                                    } else {
                                        handleChange(formItem, { key: option.key, value: [option.key] });
                                    }
                                }
                            }}
                            useComboBoxAsMenuWidth
                            calloutProps={{ calloutMaxHeight: 270 }}
                            autoComplete="on"
                            multiSelect={isCRD}
                            multiSelectDelimiter=", "
                            allowFreeform={true}
                            openOnKeyboardFocus={true}
                        />
                    </div>
                );
            } 
            else if (formItem.Caption === AGREEMENT_CATEGORY_CAPTION) {
                if(!showHiddenDropdowns) return null;

                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12" key={AGREEMENT_CATEGORY_CAPTION}>
                        <ComboBox
                            label={AGREEMENT_CATEGORY_CAPTION}
                            options={agreementCategoryOptions}
                            selectedKey={
                                realOptionKeys.length > 0 && realOptionKeys.every(k => formItem.CurrentValue.includes(k))
                                    ? [ALL_KEY, ...realOptionKeys]
                                    : formItem.CurrentValue
                            }
                            onChange={(e, option) => {
                                if (option) { handleChange(formItem, option.key); }
                            }}
                            useComboBoxAsMenuWidth
                            calloutProps={{ calloutMaxHeight: 270 }}
                            autoComplete="on"
                            allowFreeform={false}
                            multiSelect
                            multiSelectDelimiter=", "
                        />
                    </div>
                );
            }
            else if (formItem.Caption === RIGHT_TYPE_CAPTION) {
                if(!showHiddenDropdowns) return null;

                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12" key={RIGHT_TYPE_CAPTION}>
                        <ComboBox
                            label={RIGHT_TYPE_CAPTION}
                            options={formItem.Options.map((option) => ({ key: option.code, text: option.description }))}
                            selectedKey={formItem.CurrentValue}
                            onChange={(e, option) => {
                                if (option) handleChange(formItem, option.key);
                            }}
                            useComboBoxAsMenuWidth
                            calloutProps={{ calloutMaxHeight: 270 }}
                            autoComplete="on"
                            allowFreeform={false}
                        />
                    </div>
                );
            }
            else if (formItem.Caption === ROLL_UP_TERRITORIES_CAPTION) {
                if(!showHiddenDropdowns || !props.enableRollUpTerritories) return null;

                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12" key={ROLL_UP_TERRITORIES_CAPTION}>
                        <ComboBox
                            label={ROLL_UP_TERRITORIES_CAPTION}
                            options={formItem.Options.map((option) => ({ key: option.code, text: option.description }))}
                            selectedKey={formItem.CurrentValue}
                            onChange={(e, option) => {
                                if (option) handleChange(formItem, option.key);
                            }}
                            useComboBoxAsMenuWidth
                            calloutProps={{ calloutMaxHeight: 270 }}
                            autoComplete="on"
                            allowFreeform={false}
                        />
                    </div>
                );
            }
            else if (controlWithOptions) {
                optionList = formItem.Options.map((option) => 
                    ({ key: option.code, text: option.description }));
            }
            if (formItem.Type === 'combobox') {
                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12" key={formItem.Caption}>
                        <ComboBox
                            selectedKey={formItem.CurrentValue}
                            label={formItem.Caption}
                            options={optionList}
                            onChange={(e, selectedOption) => {
                                if (selectedOption) handleChange(formItem, selectedOption);
                            }}
                            useComboBoxAsMenuWidth
                            calloutProps={{ calloutMaxHeight: 270 }}
                            autoComplete="on"
                            allowFreeform={false}
                        />
                    </div>
                );
            }
            if (formItem.Type === 'text-input' && formItem.Caption === 'Member Numbers') {
                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12" key={formItem.Caption}>
                        <div className="dropdownDataInput">
                            <TextDataInput
                                label={formItem.Caption}
                                fieldName={formItem.Caption}
                                changeData={(text) => handleChange(formItem, text)}
                                value={formItem.CurrentValue as string}
                                isHidden={false}
                                isMandatory={false}
                                readOnly={formItem.Disabled}
                            />
                        </div>
                    </div>
                );
            }
            return null;
        }).filter(Boolean);

        return components;
    };

    return (
        <div className="form-group col-md-12 col-sm-12 col-xs-12">
            <div className="alert alert-warning alert-dismissible">
                <span>{'Please ensure that you have ran the Undo GSD job for the selected parameters if required'}</span>
            </div>
            {generalError.length > 0 && (
                <div className="alert alert-danger alert-dismissible">
                    <span>{generalError.join(', ')}</span>
                    <button type="button" className="close" aria-label="Close" onClick={() => setGeneralError([])}>
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            )}
            <div className="row">{data && getFormComponents(data)}</div>
            <div className="row">
                <SaveAndClose
                    manualJobParameters={props.manualJobParameters}
                    triggerManualJob={handleTriggerManualJob}
                    createNewJob={props.createNewJob}
                    scheduledJobSet={props.scheduledJobSet}
                    onError={props.onError}
                />
            </div>
        </div>
    );
};

export default GenerateStatementDetailsParameters;